import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  departmentsService,
  type DepartmentFilters,
  type DepartmentCreateData,
  type DepartmentUpdateData,
  type TeamCreateData,
  type TeamUpdateData,
} from '@/services/departments.service';
import { toast } from 'sonner';

interface DepartmentQueryOptions {
  enabled?: boolean;
}

/**
 * Query keys for department-related queries
 */
export const departmentKeys = {
  all: ['org', 'departments'] as const,
  lists: () => [...departmentKeys.all, 'list'] as const,
  list: (filters?: DepartmentFilters) => [...departmentKeys.lists(), filters] as const,
  details: () => [...departmentKeys.all, 'detail'] as const,
  detail: (id: string) => [...departmentKeys.details(), id] as const,
  stats: () => [...departmentKeys.all, 'stats'] as const,
  teams: (departmentId: string) => [...departmentKeys.all, 'teams', departmentId] as const,
  employees: (departmentId: string) => [...departmentKeys.all, 'employees', departmentId] as const,
  hierarchy: () => [...departmentKeys.all, 'hierarchy'] as const,
};

/**
 * Hook to fetch paginated departments
 */
export function useDepartments(filters?: DepartmentFilters, options: DepartmentQueryOptions = {}) {
  return useQuery({
    queryKey: departmentKeys.list(filters),
    queryFn: () => departmentsService.getDepartments(filters),
    enabled: options.enabled ?? true,
  });
}

/**
 * Hook to fetch a single department by ID
 */
export function useDepartment(id: string) {
  return useQuery({
    queryKey: departmentKeys.detail(id),
    queryFn: () => departmentsService.getDepartment(id),
    enabled: !!id,
  });
}

/**
 * Hook to fetch department statistics
 */
export function useDepartmentStats() {
  return useQuery({
    queryKey: departmentKeys.stats(),
    queryFn: () => departmentsService.getDepartmentStats(),
  });
}

/**
 * Hook to fetch teams for a department
 */
export function useDepartmentTeams(departmentId: string) {
  return useQuery({
    queryKey: departmentKeys.teams(departmentId),
    queryFn: () => departmentsService.getDepartmentTeams(departmentId),
    enabled: !!departmentId,
  });
}

/**
 * Hook to fetch employees in a department
 */
export function useDepartmentEmployees(departmentId: string) {
  return useQuery({
    queryKey: departmentKeys.employees(departmentId),
    queryFn: () => departmentsService.getDepartmentEmployees(departmentId),
    enabled: !!departmentId,
  });
}

/**
 * Hook to fetch department hierarchy
 */
export function useDepartmentHierarchy() {
  return useQuery({
    queryKey: departmentKeys.hierarchy(),
    queryFn: () => departmentsService.getDepartmentHierarchy(),
  });
}

/**
 * Hook to create a new department
 */
export function useCreateDepartment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: DepartmentCreateData) => departmentsService.createDepartment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: departmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: departmentKeys.stats() });
      queryClient.invalidateQueries({ queryKey: departmentKeys.hierarchy() });
      toast.success('Department Created', {
        description: 'The department has been successfully added to the organization',
      });
    },
    onError: (error: Error) => {
      toast.error('Failed to Create Department', {
        description: error.message || 'An error occurred while creating the department',
      });
    },
  });
}

/**
 * Hook to update a department
 */
export function useUpdateDepartment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: DepartmentUpdateData }) =>
      departmentsService.updateDepartment(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: departmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: departmentKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: departmentKeys.stats() });
      toast.success('Department Updated', {
        description: 'The department information has been successfully updated',
      });
    },
    onError: (error: Error) => {
      toast.error('Failed to Update Department', {
        description: error.message || 'An error occurred while updating the department',
      });
    },
  });
}

/**
 * Hook to delete a department
 */
export function useDeleteDepartment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => departmentsService.deleteDepartment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: departmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: departmentKeys.stats() });
      queryClient.invalidateQueries({ queryKey: departmentKeys.hierarchy() });
      toast.success('Department Deleted', {
        description: 'The department has been removed from the organization',
      });
    },
    onError: (error: Error) => {
      toast.error('Failed to Delete Department', {
        description: error.message || 'An error occurred while deleting the department',
      });
    },
  });
}

/**
 * Hook to create a team within a department
 */
export function useCreateTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: TeamCreateData) => departmentsService.createTeam(data),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: departmentKeys.teams(variables.departmentId) });
      queryClient.invalidateQueries({ queryKey: departmentKeys.detail(variables.departmentId) });
      queryClient.invalidateQueries({ queryKey: departmentKeys.stats() });
      toast.success('Team Created', {
        description: 'The team has been successfully added to the department',
      });
    },
    onError: (error: Error) => {
      toast.error('Failed to Create Team', {
        description: error.message || 'An error occurred while creating the team',
      });
    },
  });
}

/**
 * Hook to update a team
 */
export function useUpdateTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      departmentId,
      teamId,
      data,
    }: {
      departmentId: string;
      teamId: string;
      data: TeamUpdateData;
    }) => departmentsService.updateTeam(departmentId, teamId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: departmentKeys.teams(variables.departmentId) });
      queryClient.invalidateQueries({ queryKey: departmentKeys.detail(variables.departmentId) });
      toast.success('Team Updated', {
        description: 'The team information has been successfully updated',
      });
    },
    onError: (error: Error) => {
      toast.error('Failed to Update Team', {
        description: error.message || 'An error occurred while updating the team',
      });
    },
  });
}

/**
 * Hook to delete a team
 */
export function useDeleteTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ departmentId, teamId }: { departmentId: string; teamId: string }) =>
      departmentsService.deleteTeam(departmentId, teamId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: departmentKeys.teams(variables.departmentId) });
      queryClient.invalidateQueries({ queryKey: departmentKeys.detail(variables.departmentId) });
      queryClient.invalidateQueries({ queryKey: departmentKeys.stats() });
      toast.success('Team Deleted', {
        description: 'The team has been removed from the department',
      });
    },
    onError: (error: Error) => {
      toast.error('Failed to Delete Team', {
        description: error.message || 'An error occurred while deleting the team',
      });
    },
  });
}

/**
 * Hook to update department budget
 */
export function useUpdateDepartmentBudget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      departmentId,
      budget,
      budgetUsed,
    }: {
      departmentId: string;
      budget: number;
      budgetUsed?: number;
    }) => departmentsService.updateBudget(departmentId, budget, budgetUsed),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: departmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: departmentKeys.detail(variables.departmentId) });
      queryClient.invalidateQueries({ queryKey: departmentKeys.stats() });
      toast.success('Budget Updated', {
        description: 'The department budget has been successfully updated',
      });
    },
    onError: (error: Error) => {
      toast.error('Failed to Update Budget', {
        description: error.message || 'An error occurred while updating the budget',
      });
    },
  });
}

/**
 * Hook to export departments
 */
export function useExportDepartments() {
  return useMutation({
    mutationFn: ({ format, filters }: { format: 'csv' | 'xlsx'; filters?: DepartmentFilters }) =>
      departmentsService.exportDepartments(format, filters),
    onSuccess: (blob, variables) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `departments.${variables.format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Export Complete', {
        description: 'Departments data has been exported successfully',
      });
    },
    onError: (error: Error) => {
      toast.error('Export Failed', {
        description: error.message || 'An error occurred while exporting departments',
      });
    },
  });
}
